import { ref } from 'vue'
import type { Ref } from 'vue'
import { submitContactMessage } from '@/api/contactMessages'
import { useLocale } from '@/composables/useLocale'
import type { ContactMessagePayload } from '@/types/contact'

export type ContactGeneralError =
  | 'contact.form.errors.throttled'
  | 'contact.form.errors.generic'

export type ContactMessageInput = Omit<ContactMessagePayload, 'locale'>

const KNOWN_FIELDS: readonly string[] = ['name', 'email', 'topic', 'message', 'organisation_name']

function getStatus(e: unknown): number | null {
  if (typeof e !== 'object' || e === null || !('response' in e)) return null
  const response = (e as { response: unknown }).response
  if (typeof response !== 'object' || response === null || !('status' in response)) return null
  const status = (response as { status: unknown }).status
  return typeof status === 'number' ? status : null
}

function getFieldErrors(e: unknown): Record<string, string[]> {
  const response = (e as { response?: { data?: { errors?: Record<string, string[]> } } }).response
  return response?.data?.errors ?? {}
}

export function useContactMessage(): {
  submitting: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  generalError: Ref<ContactGeneralError | null>
  submit: (input: ContactMessageInput) => Promise<boolean>
  reset: () => void
} {
  const { currentLocale } = useLocale()
  const submitting = ref(false)
  const errors = ref<Record<string, string[]>>({})
  const generalError = ref<ContactGeneralError | null>(null)

  async function submit(input: ContactMessageInput): Promise<boolean> {
    submitting.value = true
    errors.value = {}
    generalError.value = null

    try {
      await submitContactMessage({ ...input, locale: currentLocale.value })
      return true
    } catch (e: unknown) {
      const status = getStatus(e)
      if (status === 422) {
        const fieldErrors = getFieldErrors(e)
        const hasKnownField = Object.keys(fieldErrors).some((key) => KNOWN_FIELDS.includes(key))
        errors.value = fieldErrors
        // Errors only on fields without an input (e.g. locale) would fail silently.
        if (!hasKnownField) generalError.value = 'contact.form.errors.generic'
      } else if (status === 429) {
        generalError.value = 'contact.form.errors.throttled'
      } else {
        generalError.value = 'contact.form.errors.generic'
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  function reset(): void {
    errors.value = {}
    generalError.value = null
  }

  return { submitting, errors, generalError, submit, reset }
}
